// Communication-chart cascade registry — shared between the editor-pane
// chart controller (chart cells) and the wizard-pane picker (cascading
// option grid). The shapes mirror the standalone comm_chart.html mockup
// so the live editor behaves identically.
//
// Picking a value in column N narrows column N+1 to a curated subset via
// the `*_BY_*` override maps. When no override exists for a parent pick
// (i.e. you typed something custom), the generic groups stand in.

export const COLUMN_ORDER = ["happening", "behavior", "meaning", "support"]

export const COLUMN_TITLE = {
  happening: { icon: "alert-circle",   title: "What's happening?" },
  behavior:  { icon: "activity",       title: "What behavior do you see?" },
  meaning:   { icon: "message-circle", title: "What might it mean?" },
  support:   { icon: "hand-helping",   title: "How will you support?" }
}

export const GROUP_ICON = {
  "Daily routines":           "calendar",
  "Sensory triggers":         "alert-triangle",
  "Transitions":              "shuffle",
  "Social moments":           "users",
  "Emotional states":         "heart",
  "School & learning":        "school",
  "Outings & travel":         "car",
  "Health & body":            "stethoscope",
  "Family events":            "home",
  "Body responses":           "user",
  "Vocal responses":          "mic",
  "Active responses":         "footprints",
  "Withdrawal":               "door-closed",
  "Self-stimulation":         "activity",
  "Requesting / asking":      "hand",
  "Sensory":                  "alert-triangle",
  "Emotional":                "heart",
  "Physical":                 "thermometer",
  "Communication need":       "message-circle",
  "Avoidance":                "x-circle",
  "Social need":              "users",
  "Routine / predictability": "calendar",
  "Calming strategies":       "wind",
  "Connection":               "users",
  "Environment adjustments":  "settings",
  "Visual supports":          "image",
  "Sensory tools":            "package",
  "Communication supports":   "message-square",
  "Routine supports":         "calendar-clock",
  "Breaks & space":           "pause"
}

// Default groups to open in the picker when no parent context exists.
export const DEFAULT_OPEN_BY_COL = {
  happening: ["Daily routines", "Sensory triggers"],
  behavior:  ["Body responses",  "Vocal responses"],
  meaning:   ["Sensory",         "Emotional"],
  support:   ["Calming strategies", "Connection"]
}

// Context-aware example hints per seeded "happening". The editor chart
// shows these as grey placeholder text; the wizard picker surfaces the
// matching one as a clickable "Suggested" option so the good example is
// part of the selection (not just a hint). Shared so both stay in sync.
export const HINTS = {
  "Hungry or thirsty": {
    behavior: "e.g. Goes to the kitchen, points at fridge",
    meaning:  "e.g. Wants a snack or drink",
    support:  "e.g. Offer two choices and let them pick"
  },
  "Tired or needs rest": {
    behavior: "e.g. Lays head down, rubs eyes, gets cranky",
    meaning:  "e.g. Needs a break or nap",
    support:  "e.g. Offer a quiet space to rest"
  },
  "In pain or not feeling well": {
    behavior: "e.g. Cries, holds body part, withdraws",
    meaning:  "e.g. Something hurts or feels sick",
    support:  "e.g. Check in gently, look for signs of injury"
  },
  "Frustrated or upset": {
    behavior: "e.g. Raises voice, throws things, shuts down",
    meaning:  "e.g. Feeling overwhelmed or stuck",
    support:  "e.g. Stay calm, offer help or a break"
  },
  "Overwhelmed by noise or crowds": {
    behavior: "e.g. Covers ears, moves away, gets anxious",
    meaning:  "e.g. Environment is too stimulating",
    support:  "e.g. Offer headphones or a quiet space"
  }
}

// The suggested value for a (happening, col) cell with the "e.g. " prefix
// stripped so it's usable as a real selectable value. "" when no context
// hint applies (e.g. the happening column itself, or a custom happening).
export function commHintFor(happening, col) {
  const h = HINTS[happening]
  const raw = h && h[col]
  return raw ? raw.replace(/^e\.g\.\s*/i, "").trim() : ""
}

// Most-likely options per column — surfaced as "Quick picks" at the top
// of the picker pane.
export const QUICK_BY_COL = {
  happening: ["Loud noises", "Meal time", "Bedtime", "Change in plans", "Doctor visit", "Crowded spaces"],
  behavior:  ["Cries", "Covers ears", "Runs away", "Hides", "Clings to caregiver", "Goes still"],
  meaning:   ["Sensory overload", "I'm scared", "I want a break", "I'm tired", "I need help", "Routine disrupted"],
  support:   ["Move to quiet space", "Offer a hug", "Give 2-minute warning", "Show visual schedule", "Breathing exercise", "Take a break"]
}

/* ============================================================
   SITUATIONS — 9 groups, ~85 items
   ============================================================ */
export const SITUATIONS = {
  "Daily routines": [
    { value: "Meal time",         icon: "utensils",       color: "g-daily" },
    { value: "Bedtime",           icon: "moon",           color: "g-daily" },
    { value: "Bath time",         icon: "shower-head",    color: "g-daily" },
    { value: "Getting dressed",   icon: "shirt",          color: "g-daily" },
    { value: "Taking medication", icon: "pill",           color: "g-daily" },
    { value: "Brushing teeth",    icon: "smile",          color: "g-daily" },
    { value: "Hair brushing",     icon: "scissors",       color: "g-daily" },
    { value: "Toileting",         icon: "toilet",         color: "g-daily" },
    { value: "Hand washing",      icon: "droplets",       color: "g-daily" },
    { value: "Wake up",           icon: "alarm-clock",    color: "g-daily" }
  ],
  "Sensory triggers": [
    { value: "Loud noises",       icon: "volume-2",       color: "g-sensory" },
    { value: "Bright lights",     icon: "sun",            color: "g-sensory" },
    { value: "Strong smells",     icon: "wind",           color: "g-sensory" },
    { value: "Crowded spaces",    icon: "users",          color: "g-sensory" },
    { value: "Tight clothing",    icon: "shirt",          color: "g-sensory" },
    { value: "Wet clothes",       icon: "droplets",       color: "g-sensory" },
    { value: "Sudden touch",      icon: "hand",           color: "g-sensory" },
    { value: "Itchy fabric",      icon: "alert-triangle", color: "g-sensory" },
    { value: "Loud chewing",      icon: "ear",            color: "g-sensory" },
    { value: "Hot temperature",   icon: "thermometer",    color: "g-sensory" },
    { value: "Cold temperature",  icon: "snowflake",      color: "g-sensory" }
  ],
  "Transitions": [
    { value: "Going out",         icon: "car",            color: "g-transition" },
    { value: "Coming home",       icon: "home",           color: "g-transition" },
    { value: "Change in plans",   icon: "shuffle",        color: "g-transition" },
    { value: "End of activity",   icon: "stop-circle",    color: "g-transition" },
    { value: "Start of activity", icon: "play-circle",    color: "g-transition" },
    { value: "Going to school",   icon: "school",         color: "g-transition" },
    { value: "Leaving school",    icon: "door-open",      color: "g-transition" },
    { value: "Bedtime transition", icon: "moon",          color: "g-transition" },
    { value: "Screen time ends",  icon: "smartphone",     color: "g-transition" },
    { value: "New caregiver",     icon: "user-plus",      color: "g-transition" }
  ],
  "Social moments": [
    { value: "Stranger approaches", icon: "user-plus",    color: "g-social" },
    { value: "Sibling argument",    icon: "users",        color: "g-social" },
    { value: "Birthday party",      icon: "party-popper", color: "g-social" },
    { value: "Holiday gathering",   icon: "gift",         color: "g-social" },
    { value: "Playdate",            icon: "smile",        color: "g-social" },
    { value: "Doctor visit",        icon: "stethoscope",  color: "g-social" },
    { value: "Class presentation",  icon: "mic",          color: "g-social" },
    { value: "Group activity",      icon: "users",        color: "g-social" },
    { value: "Sharing toys",        icon: "hand",         color: "g-social" },
    { value: "Picture day",         icon: "camera",       color: "g-social" }
  ],
  "Emotional states": [
    { value: "Sad / hurt",        icon: "frown",          color: "g-emotion" },
    { value: "Excited",           icon: "sparkles",       color: "g-emotion" },
    { value: "Anxious",           icon: "cloud-rain",     color: "g-emotion" },
    { value: "Tired",             icon: "battery-low",    color: "g-emotion" },
    { value: "Frustrated",        icon: "zap",            color: "g-emotion" },
    { value: "Overwhelmed",       icon: "cloud",          color: "g-emotion" },
    { value: "Lonely",            icon: "user",           color: "g-emotion" },
    { value: "Angry",             icon: "flame",          color: "g-emotion" },
    { value: "Confused",          icon: "help-circle",    color: "g-emotion" },
    { value: "Bored",             icon: "moon",           color: "g-emotion" }
  ],
  "School & learning": [
    { value: "In class",          icon: "book-open",      color: "g-school" },
    { value: "Test / quiz",       icon: "clipboard-check", color: "g-school" },
    { value: "Reading aloud",     icon: "book-open",      color: "g-school" },
    { value: "PE / gym class",    icon: "dumbbell",       color: "g-school" },
    { value: "Lunch room",        icon: "utensils",       color: "g-school" },
    { value: "Recess",            icon: "smile",          color: "g-school" },
    { value: "Fire drill",        icon: "alarm-clock",    color: "g-school" },
    { value: "Substitute teacher", icon: "user-x",        color: "g-school" },
    { value: "Bus ride",          icon: "bus",            color: "g-school" },
    { value: "Homework time",     icon: "pencil",         color: "g-school" }
  ],
  "Outings & travel": [
    { value: "Grocery store",     icon: "shopping-cart",  color: "g-outing" },
    { value: "Restaurant",        icon: "utensils",       color: "g-outing" },
    { value: "Long car ride",     icon: "car",            color: "g-outing" },
    { value: "Airport / flight",  icon: "plane",          color: "g-outing" },
    { value: "Movie theater",     icon: "film",           color: "g-outing" },
    { value: "Mall",              icon: "shopping-bag",   color: "g-outing" },
    { value: "Park / playground", icon: "trees",          color: "g-outing" },
    { value: "Vacation",          icon: "luggage",        color: "g-outing" },
    { value: "Waiting in line",   icon: "clock",          color: "g-outing" },
    { value: "Public transit",    icon: "train-front",    color: "g-outing" }
  ],
  "Health & body": [
    { value: "Hair cut",          icon: "scissors",       color: "g-health" },
    { value: "Nail trim",         icon: "scissors",       color: "g-health" },
    { value: "Vaccination",       icon: "syringe",        color: "g-health" },
    { value: "Sick day",          icon: "thermometer",    color: "g-health" },
    { value: "Hungry",            icon: "utensils-crossed", color: "g-health" },
    { value: "Thirsty",           icon: "droplets",       color: "g-health" },
    { value: "Bathroom urgency",  icon: "toilet",         color: "g-health" },
    { value: "Headache",          icon: "alert-circle",   color: "g-health" },
    { value: "Stomach ache",      icon: "alert-circle",   color: "g-health" }
  ],
  "Family events": [
    { value: "Argument at home",  icon: "users",          color: "g-family" },
    { value: "New baby",          icon: "baby",           color: "g-family" },
    { value: "Moving house",      icon: "truck",          color: "g-family" },
    { value: "Family member ill", icon: "stethoscope",    color: "g-family" },
    { value: "Pet pass away",     icon: "heart-crack",    color: "g-family" },
    { value: "Divorce / separation", icon: "scissors",    color: "g-family" },
    { value: "Visitor stays over", icon: "home",          color: "g-family" }
  ]
}

/* ============================================================
   BEHAVIORS — generic + per-situation overrides
   ============================================================ */
export const BEHAVIORS_GENERIC = {
  "Body responses": [
    { value: "Covers ears",        icon: "ear-off",        color: "g-body" },
    { value: "Covers eyes",        icon: "eye-off",        color: "g-body" },
    { value: "Goes still",         icon: "pause",          color: "g-body" },
    { value: "Jumps / flaps",      icon: "sparkles",       color: "g-body" },
    { value: "Squints / blinks",   icon: "eye",            color: "g-body" },
    { value: "Pulls at clothes",   icon: "shirt",          color: "g-body" },
    { value: "Tenses up",          icon: "alert-triangle", color: "g-body" },
    { value: "Curls into ball",    icon: "circle",         color: "g-body" }
  ],
  "Vocal responses": [
    { value: "Cries",              icon: "frown",          color: "g-vocal" },
    { value: "Yells / shouts",     icon: "megaphone",      color: "g-vocal" },
    { value: "Repeats phrase",     icon: "repeat",         color: "g-vocal" },
    { value: "Goes silent",        icon: "volume-x",       color: "g-vocal" },
    { value: "Asks 'why'",         icon: "help-circle",    color: "g-vocal" },
    { value: "Hums / sings",       icon: "music",          color: "g-vocal" },
    { value: "Whispers",           icon: "volume-1",       color: "g-vocal" },
    { value: "Screams",            icon: "megaphone",      color: "g-vocal" }
  ],
  "Active responses": [
    { value: "Runs away",          icon: "footprints",     color: "g-action" },
    { value: "Hits",               icon: "hand",           color: "g-action" },
    { value: "Throws items",       icon: "trash-2",        color: "g-action" },
    { value: "Clings to caregiver", icon: "user-plus",     color: "g-action" },
    { value: "Asks to leave",      icon: "door-open",      color: "g-action" },
    { value: "Paces",              icon: "footprints",     color: "g-action" },
    { value: "Bolts / elopes",     icon: "zap",            color: "g-action" }
  ],
  "Withdrawal": [
    { value: "Hides",                  icon: "door-closed",   color: "g-withdraw" },
    { value: "Goes to quiet space",    icon: "moon",          color: "g-withdraw" },
    { value: "Withdraws / shuts down", icon: "cloud-rain",    color: "g-withdraw" },
    { value: "Becomes very quiet",     icon: "volume-1",      color: "g-withdraw" },
    { value: "Falls asleep suddenly",  icon: "moon",          color: "g-withdraw" },
    { value: "Avoids eye contact",     icon: "eye-off",       color: "g-withdraw" }
  ],
  "Self-stimulation": [
    { value: "Rocks body",        icon: "activity",       color: "g-stim" },
    { value: "Spins / twirls",    icon: "rotate-cw",      color: "g-stim" },
    { value: "Hand flapping",     icon: "hand",           color: "g-stim" },
    { value: "Chews on objects",  icon: "circle",         color: "g-stim" },
    { value: "Repetitive tapping", icon: "music",         color: "g-stim" },
    { value: "Picks at skin",     icon: "hand",           color: "g-stim" }
  ],
  "Requesting / asking": [
    { value: "Points to object",  icon: "hand",           color: "g-ask" },
    { value: "Brings caregiver to item", icon: "user-plus", color: "g-ask" },
    { value: "Uses AAC device",   icon: "cpu",            color: "g-ask" },
    { value: "Sign language",     icon: "hand",           color: "g-ask" },
    { value: "Picture exchange",  icon: "image",          color: "g-ask" }
  ]
}

export const BEHAVIORS_BY_SITUATION = {
  "Loud noises": {
    "Body responses": [
      { value: "Covers ears",       icon: "ear-off",      color: "g-body" },
      { value: "Holds head",        icon: "headphones",   color: "g-body" },
      { value: "Asks for ear muffs", icon: "headphones",  color: "g-body" },
      { value: "Goes still",        icon: "pause",        color: "g-body" }
    ],
    "Vocal responses": [
      { value: "Cries",             icon: "frown",        color: "g-vocal" },
      { value: "Goes silent",       icon: "volume-x",     color: "g-vocal" }
    ],
    "Active responses": [
      { value: "Runs away",         icon: "footprints",   color: "g-action" },
      { value: "Asks to leave",     icon: "door-open",    color: "g-action" }
    ]
  },
  "Bright lights": {
    "Body responses": [
      { value: "Squints",            icon: "eye-off",    color: "g-body" },
      { value: "Covers eyes",        icon: "eye-off",    color: "g-body" },
      { value: "Asks for sunglasses", icon: "glasses",   color: "g-body" }
    ],
    "Withdrawal": [
      { value: "Goes to quiet space", icon: "moon",      color: "g-withdraw" }
    ]
  },
  "Strong smells": {
    "Body responses": [
      { value: "Pinches nose",       icon: "user-x",     color: "g-body" },
      { value: "Gags",               icon: "alert-triangle", color: "g-body" },
      { value: "Holds breath",       icon: "wind",       color: "g-body" }
    ],
    "Active responses": [
      { value: "Asks to leave",      icon: "door-open",  color: "g-action" }
    ]
  },
  "Crowded spaces": {
    "Active responses": [
      { value: "Clings to caregiver", icon: "user-plus", color: "g-action" },
      { value: "Asks to leave",       icon: "door-open", color: "g-action" }
    ],
    "Withdrawal": [
      { value: "Hides behind parent", icon: "door-closed", color: "g-withdraw" },
      { value: "Becomes very quiet",  icon: "volume-1",    color: "g-withdraw" }
    ]
  },
  "Meal time": {
    "Body responses": [
      { value: "Refuses to eat",     icon: "x-circle",         color: "g-body" },
      { value: "Eats only one food", icon: "utensils-crossed", color: "g-body" },
      { value: "Throws food",        icon: "trash-2",          color: "g-body" },
      { value: "Won't sit",          icon: "footprints",       color: "g-body" }
    ],
    "Vocal responses": [
      { value: "Asks for different food", icon: "utensils",    color: "g-vocal" }
    ]
  },
  "Bedtime": {
    "Body responses": [
      { value: "Gets out of bed",     icon: "arrow-up",  color: "g-body" },
      { value: "Stalls",              icon: "clock",     color: "g-body" }
    ],
    "Vocal responses": [
      { value: "Calls for parent",    icon: "phone",     color: "g-vocal" },
      { value: "Cries",               icon: "frown",     color: "g-vocal" },
      { value: "Asks for water",      icon: "droplets",  color: "g-vocal" }
    ]
  },
  "Change in plans": {
    "Body responses": [
      { value: "Resists",            icon: "shield",       color: "g-body" },
      { value: "Goes still",         icon: "pause",        color: "g-body" },
      { value: "Has meltdown",       icon: "cloud-rain",   color: "g-body" }
    ],
    "Vocal responses": [
      { value: "Asks 'why'",          icon: "help-circle",  color: "g-vocal" },
      { value: "Repeats prior plan",  icon: "repeat",       color: "g-vocal" }
    ]
  },
  "Doctor visit": {
    "Body responses": [
      { value: "Clings to caregiver", icon: "user-plus",   color: "g-body" },
      { value: "Holds breath",        icon: "wind",        color: "g-body" },
      { value: "Tenses up",           icon: "alert-triangle", color: "g-body" }
    ],
    "Vocal responses": [
      { value: "Cries",               icon: "frown",       color: "g-vocal" }
    ]
  },
  "Hair cut": {
    "Body responses": [
      { value: "Cries",         icon: "frown",          color: "g-body" },
      { value: "Tenses up",     icon: "alert-triangle", color: "g-body" },
      { value: "Pulls away",    icon: "user-x",         color: "g-body" }
    ]
  },
  "Tired": {
    "Body responses": [
      { value: "Goes still",          icon: "pause",     color: "g-body" }
    ],
    "Withdrawal": [
      { value: "Falls asleep suddenly", icon: "moon",    color: "g-withdraw" },
      { value: "Becomes very quiet",  icon: "volume-1",  color: "g-withdraw" }
    ]
  },
  "Frustrated": {
    "Vocal responses": [
      { value: "Yells / shouts",      icon: "megaphone", color: "g-vocal" }
    ],
    "Active responses": [
      { value: "Throws items",        icon: "trash-2",   color: "g-action" },
      { value: "Hits",                icon: "hand",      color: "g-action" }
    ]
  },
  "Sad / hurt": {
    "Vocal responses": [
      { value: "Cries",               icon: "frown",     color: "g-vocal" }
    ],
    "Requesting / asking": [
      { value: "Wants a hug",         icon: "heart",     color: "g-ask" }
    ],
    "Withdrawal": [
      { value: "Withdraws / shuts down", icon: "cloud-rain", color: "g-withdraw" }
    ]
  }
}

/* ============================================================
   MEANINGS — generic + per-behavior overrides
   ============================================================ */
export const MEANINGS_GENERIC = {
  "Sensory": [
    { value: "Sensory overload",    icon: "alert-circle",   color: "g-sensory" },
    { value: "I need quiet",        icon: "volume-x",       color: "g-sensory" },
    { value: "I'm overwhelmed",     icon: "cloud-rain",     color: "g-sensory" },
    { value: "Too bright",          icon: "sun",            color: "g-sensory" },
    { value: "Too loud",            icon: "volume-2",       color: "g-sensory" },
    { value: "Too crowded",         icon: "users",          color: "g-sensory" }
  ],
  "Emotional": [
    { value: "I'm sad",             icon: "frown",          color: "g-emotion" },
    { value: "I'm scared",          icon: "ghost",          color: "g-emotion" },
    { value: "I'm angry",           icon: "flame",          color: "g-emotion" },
    { value: "I feel unsafe",       icon: "shield",         color: "g-emotion" },
    { value: "I'm anxious",         icon: "cloud-rain",     color: "g-emotion" }
  ],
  "Physical": [
    { value: "I'm tired",           icon: "battery-low",    color: "g-emotion" },
    { value: "I'm hungry",          icon: "utensils",       color: "g-emotion" },
    { value: "I'm thirsty",         icon: "droplets",       color: "g-emotion" },
    { value: "I'm in pain",         icon: "alert-circle",   color: "g-emotion" }
  ],
  "Communication need": [
    { value: "I need help",         icon: "help-circle",    color: "g-comm" },
    { value: "I want attention",    icon: "user-plus",      color: "g-comm" },
    { value: "I want this item",    icon: "hand",           color: "g-comm" },
    { value: "I want a break",      icon: "pause",          color: "g-comm" }
  ],
  "Routine / predictability": [
    { value: "Routine disrupted",   icon: "alert-triangle", color: "g-routine" },
    { value: "Needs warning",       icon: "bell",           color: "g-routine" }
  ]
}

export const MEANINGS_BY_BEHAVIOR = {
  "Covers ears": {
    "Sensory": [
      { value: "Sensory overload",   icon: "alert-circle",  color: "g-sensory" },
      { value: "Sound is too loud",  icon: "volume-2",      color: "g-sensory" },
      { value: "I need quiet",       icon: "volume-x",      color: "g-sensory" }
    ]
  },
  "Covers eyes": {
    "Sensory": [
      { value: "Too bright",         icon: "sun",           color: "g-sensory" },
      { value: "I'm overwhelmed",    icon: "cloud-rain",    color: "g-sensory" }
    ]
  },
  "Brings caregiver to item": {
    "Physical": [
      { value: "I'm hungry",          icon: "utensils",       color: "g-emotion" },
      { value: "I'm thirsty",         icon: "droplets",       color: "g-emotion" }
    ],
    "Communication need": [
      { value: "I want this item",    icon: "hand",           color: "g-comm" },
      { value: "I need help",         icon: "help-circle",    color: "g-comm" }
    ]
  },
  "Points to food": {
    "Physical": [
      { value: "I'm hungry",          icon: "utensils",       color: "g-emotion" },
      { value: "I'm thirsty",         icon: "droplets",       color: "g-emotion" }
    ],
    "Communication need": [
      { value: "Wants different food", icon: "utensils",     color: "g-comm" }
    ]
  },
  "Opens fridge / pantry": {
    "Physical": [
      { value: "I'm hungry",          icon: "utensils",       color: "g-emotion" },
      { value: "I'm thirsty",         icon: "droplets",       color: "g-emotion" }
    ]
  },
  "Rubs tummy": {
    "Physical": [
      { value: "I'm hungry",          icon: "utensils",       color: "g-emotion" },
      { value: "Stomach ache",        icon: "alert-circle",   color: "g-emotion" }
    ]
  },
  "Refuses to eat": {
    "Physical": [
      { value: "Not hungry",         icon: "x-circle",       color: "g-emotion" },
      { value: "Texture aversion",   icon: "alert-triangle", color: "g-emotion" }
    ],
    "Communication need": [
      { value: "Wants different food", icon: "utensils",    color: "g-comm" }
    ]
  },
  "Cries": {
    "Emotional": [
      { value: "I'm sad",            icon: "frown",         color: "g-emotion" },
      { value: "I'm scared",         icon: "ghost",         color: "g-emotion" },
      { value: "I'm overwhelmed",    icon: "cloud-rain",    color: "g-emotion" }
    ],
    "Physical": [
      { value: "I'm in pain",        icon: "alert-circle",  color: "g-emotion" },
      { value: "I'm tired",          icon: "battery-low",   color: "g-emotion" }
    ]
  },
  "Resists": {
    "Routine / predictability": [
      { value: "Routine disrupted",  icon: "alert-triangle", color: "g-routine" },
      { value: "Needs warning",      icon: "bell",            color: "g-routine" }
    ],
    "Communication need": [
      { value: "I'm not ready",      icon: "clock",           color: "g-comm" }
    ]
  },
  "Runs away": {
    "Communication need": [
      { value: "I want to leave",    icon: "door-open",     color: "g-comm" }
    ],
    "Emotional": [
      { value: "I'm scared",         icon: "ghost",         color: "g-emotion" }
    ]
  },
  "Hits": {
    "Emotional": [
      { value: "I'm angry",          icon: "flame",         color: "g-emotion" },
      { value: "I'm frustrated",     icon: "zap",           color: "g-emotion" }
    ],
    "Communication need": [
      { value: "I want attention",   icon: "user-plus",     color: "g-comm" }
    ]
  },
  "Jumps / flaps": {
    "Emotional": [
      { value: "I'm excited",        icon: "sparkles",       color: "g-emotion" }
    ],
    "Sensory": [
      { value: "Body regulation",    icon: "activity",       color: "g-sensory" }
    ]
  },
  "Hides": {
    "Emotional": [
      { value: "I feel unsafe",      icon: "shield",         color: "g-emotion" },
      { value: "I'm scared",         icon: "ghost",          color: "g-emotion" }
    ]
  },
  "Clings to caregiver": {
    "Emotional": [
      { value: "I need reassurance", icon: "heart",          color: "g-emotion" },
      { value: "I feel unsafe",      icon: "shield",         color: "g-emotion" }
    ]
  },
  "Rocks body": {
    "Sensory": [
      { value: "Self-soothing",      icon: "heart",          color: "g-sensory" },
      { value: "Body regulation",    icon: "activity",       color: "g-sensory" }
    ]
  },
  "Tenses up": {
    "Emotional": [
      { value: "I'm anxious",        icon: "cloud-rain",    color: "g-emotion" },
      { value: "I'm scared",         icon: "ghost",         color: "g-emotion" }
    ]
  },
  "Asks 'why'": {
    "Routine / predictability": [
      { value: "Routine disrupted",  icon: "alert-triangle", color: "g-routine" }
    ],
    "Communication need": [
      { value: "I don't understand", icon: "help-circle",   color: "g-comm" }
    ]
  }
}

/* ============================================================
   SUPPORTS — generic + per-meaning overrides
   ============================================================ */
export const SUPPORTS_GENERIC = {
  "Calming strategies": [
    { value: "Move to quiet space",   icon: "door-open",      color: "g-calm" },
    { value: "Offer noise-canceling headphones", icon: "headphones", color: "g-calm" },
    { value: "Deep pressure / weighted blanket", icon: "shield", color: "g-calm" },
    { value: "Breathing exercise",    icon: "wind",           color: "g-calm" },
    { value: "Squeeze ball / fidget", icon: "circle",         color: "g-calm" }
  ],
  "Connection": [
    { value: "Offer a hug",            icon: "heart",          color: "g-connect" },
    { value: "Hold hand",              icon: "hand",           color: "g-connect" },
    { value: "Sit nearby",             icon: "users",          color: "g-connect" },
    { value: "Validate feelings",      icon: "message-circle", color: "g-connect" },
    { value: "Use calm voice",         icon: "volume-1",       color: "g-connect" }
  ],
  "Environment adjustments": [
    { value: "Dim the lights",         icon: "sun-dim",        color: "g-env" },
    { value: "Lower the volume",       icon: "volume-1",       color: "g-env" },
    { value: "Remove trigger",         icon: "x-circle",       color: "g-env" }
  ],
  "Visual supports": [
    { value: "Show visual schedule",   icon: "image",          color: "g-visual" },
    { value: "Give 2-minute warning",  icon: "bell",           color: "g-visual" },
    { value: "Use first-then board",   icon: "list-ordered",   color: "g-visual" },
    { value: "Offer choice cards",     icon: "list-checks",    color: "g-visual" },
    { value: "Set a timer",            icon: "timer",          color: "g-visual" }
  ],
  "Breaks & space": [
    { value: "Take a break",           icon: "pause",          color: "g-break" },
    { value: "Offer break card",       icon: "credit-card",    color: "g-break" },
    { value: "Quiet corner",           icon: "home",           color: "g-break" }
  ]
}

export const SUPPORTS_BY_MEANING = {
  "Sensory overload": {
    "Calming strategies": [
      { value: "Offer noise-canceling headphones", icon: "headphones", color: "g-calm" },
      { value: "Move to quiet space", icon: "door-open",     color: "g-calm" },
      { value: "Deep pressure",       icon: "shield",        color: "g-calm" }
    ],
    "Environment adjustments": [
      { value: "Dim the lights",      icon: "sun-dim",       color: "g-env" },
      { value: "Lower the volume",    icon: "volume-1",      color: "g-env" }
    ]
  },
  "I need quiet": {
    "Environment adjustments": [
      { value: "Lower the volume",    icon: "volume-1",      color: "g-env" },
      { value: "Move to quiet space", icon: "door-open",     color: "g-env" }
    ]
  },
  "Too bright": {
    "Environment adjustments": [
      { value: "Dim the lights",      icon: "sun-dim",       color: "g-env" }
    ]
  },
  "Too loud": {
    "Calming strategies": [
      { value: "Offer noise-canceling headphones", icon: "headphones", color: "g-calm" }
    ],
    "Environment adjustments": [
      { value: "Lower the volume",    icon: "volume-1",      color: "g-env" },
      { value: "Move to quiet space", icon: "door-open",     color: "g-env" }
    ]
  },
  "I'm overwhelmed": {
    "Calming strategies": [
      { value: "Move to quiet space", icon: "door-open",     color: "g-calm" },
      { value: "Breathing exercise",  icon: "wind",          color: "g-calm" }
    ],
    "Breaks & space": [
      { value: "Take a break",        icon: "pause",         color: "g-break" }
    ]
  },
  "Routine disrupted": {
    "Visual supports": [
      { value: "Show visual schedule",  icon: "image",        color: "g-visual" },
      { value: "Give 2-minute warning", icon: "bell",         color: "g-visual" },
      { value: "Use first-then board",  icon: "list-ordered", color: "g-visual" }
    ]
  },
  "Needs warning": {
    "Visual supports": [
      { value: "Give 2-minute warning", icon: "bell",         color: "g-visual" },
      { value: "Set a timer",           icon: "timer",        color: "g-visual" }
    ]
  },
  "I'm sad": {
    "Connection": [
      { value: "Offer a hug",        icon: "heart",          color: "g-connect" },
      { value: "Validate feelings",  icon: "message-circle", color: "g-connect" },
      { value: "Sit nearby",         icon: "users",          color: "g-connect" }
    ]
  },
  "I'm scared": {
    "Connection": [
      { value: "Hold hand",         icon: "hand",          color: "g-connect" },
      { value: "Use calm voice",    icon: "volume-1",      color: "g-connect" },
      { value: "Validate feelings", icon: "message-circle", color: "g-connect" }
    ]
  },
  "I'm angry": {
    "Calming strategies": [
      { value: "Breathing exercise",   icon: "wind",         color: "g-calm" },
      { value: "Squeeze ball / fidget", icon: "circle",      color: "g-calm" }
    ],
    "Breaks & space": [
      { value: "Take a break",        icon: "pause",         color: "g-break" }
    ]
  },
  "I feel unsafe": {
    "Connection": [
      { value: "Offer a hug",        icon: "heart",          color: "g-connect" },
      { value: "Hold hand",          icon: "hand",           color: "g-connect" }
    ]
  },
  "I need reassurance": {
    "Connection": [
      { value: "Offer a hug",        icon: "heart",          color: "g-connect" },
      { value: "Validate feelings",  icon: "message-circle", color: "g-connect" }
    ]
  },
  "I'm anxious": {
    "Calming strategies": [
      { value: "Breathing exercise",   icon: "wind",         color: "g-calm" },
      { value: "Squeeze ball / fidget", icon: "circle",      color: "g-calm" }
    ]
  },
  "I'm hungry": {
    "Routine supports": [
      { value: "Offer a choice of healthy snacks", icon: "utensils",     color: "g-routine" },
      { value: "Offer scheduled meal early",        icon: "calendar-clock", color: "g-routine" }
    ],
    "Communication supports": [
      { value: "Use food PECS / picture board",     icon: "image",        color: "g-comm" },
      { value: "Model 'I want ___' sentence",       icon: "message-square", color: "g-comm" }
    ]
  },
  "I'm thirsty": {
    "Routine supports": [
      { value: "Offer water first",                 icon: "droplets",     color: "g-routine" },
      { value: "Offer favorite drink",              icon: "cup-soda",     color: "g-routine" }
    ]
  },
  "I'm tired": {
    "Breaks & space": [
      { value: "Take a break",        icon: "pause",         color: "g-break" },
      { value: "Quiet corner",        icon: "home",          color: "g-break" }
    ]
  },
  "I'm in pain": {
    "Connection": [
      { value: "Validate feelings",  icon: "message-circle", color: "g-connect" }
    ]
  },
  "I want a break": {
    "Breaks & space": [
      { value: "Take a break",       icon: "pause",         color: "g-break" },
      { value: "Offer break card",   icon: "credit-card",   color: "g-break" }
    ]
  },
  "I need help": {
    "Visual supports": [
      { value: "Offer choice cards",  icon: "list-checks",   color: "g-visual" }
    ]
  },
  "I want this item": {
    "Visual supports": [
      { value: "Offer choice cards",  icon: "list-checks",   color: "g-visual" }
    ]
  },
  "I want attention": {
    "Connection": [
      { value: "Sit nearby",         icon: "users",          color: "g-connect" }
    ]
  },
  "I want to leave": {
    "Breaks & space": [
      { value: "Take a break",       icon: "pause",         color: "g-break" }
    ]
  },
  "I'm not ready": {
    "Visual supports": [
      { value: "Set a timer",         icon: "timer",         color: "g-visual" },
      { value: "Give 2-minute warning", icon: "bell",        color: "g-visual" }
    ]
  },
  "I don't understand": {
    "Visual supports": [
      { value: "Show visual schedule", icon: "image",        color: "g-visual" }
    ]
  },
  "Body regulation": {
    "Calming strategies": [
      { value: "Deep pressure / weighted blanket", icon: "shield", color: "g-calm" }
    ]
  },
  "Self-soothing": {
    "Calming strategies": [
      { value: "Deep pressure / weighted blanket", icon: "shield", color: "g-calm" }
    ]
  }
}

// ============================================================
// Lookup helpers — used by both controllers.
// ============================================================

// Lightweight keyword → group affinity. When the picker falls back
// to MEANINGS_GENERIC (no exact behavior match), we use the situation
// + behavior text to BIAS which groups surface first, so "Hungry or
// thirsty / Points to object he wants to eat" leads with Physical +
// Communication need instead of dumping Emotional on top.
const CONTEXT_KEYWORDS = {
  "Physical": [
    "hungr", "thirst", "food", "eat", "drink", "snack", "fridge", "pantry",
    "tired", "sleep", "rest", "pain", "tummy", "stomach", "hurt", "sick",
    "cold", "hot", "bathroom", "toilet"
  ],
  "Communication need": [
    "point", "bring", "show", "want", "request", "ask", "need", "give",
    "fetch", "carry", "object", "item"
  ],
  "Sensory": [
    "loud", "noise", "bright", "light", "smell", "crowded", "texture",
    "cover", "ear", "eye", "rock", "spin", "flap"
  ],
  "Emotional": [
    "cry", "sad", "scared", "anger", "angry", "upset", "fear",
    "overwhelm", "frustrat", "anxi", "lonely", "miss"
  ],
  "Routine / predictability": [
    "routine", "schedule", "change", "transition", "warning", "predict",
    "resist", "stuck", "habit"
  ],
  "Social need": [
    "alone", "friend", "play", "share", "ignore", "join", "people"
  ]
}

function rankMeaningGroupsForContext(state) {
  const text = `${state.happening || ""} ${state.behavior || ""}`.toLowerCase()
  if (!text.trim()) return null
  const ranks = []
  for (const [group, kws] of Object.entries(CONTEXT_KEYWORDS)) {
    const score = kws.reduce((n, kw) => n + (text.includes(kw) ? 1 : 0), 0)
    if (score > 0) ranks.push([group, score])
  }
  if (!ranks.length) return null
  ranks.sort((a, b) => b[1] - a[1])
  return ranks.map(r => r[0])
}

function reorderGroups(groups, priorityNames) {
  if (!priorityNames || !priorityNames.length) return groups
  const out = {}
  // Promote priority groups first (when present in the source map),
  // then append everything else in original order so the user can
  // still find them.
  priorityNames.forEach(name => { if (groups[name]) out[name] = groups[name] })
  Object.entries(groups).forEach(([name, items]) => { if (!out[name]) out[name] = items })
  return out
}

export function optionsFor(col, state) {
  if (col === "happening") return SITUATIONS
  if (col === "behavior") {
    return BEHAVIORS_BY_SITUATION[state.happening] || BEHAVIORS_GENERIC
  }
  if (col === "meaning") {
    // 1) Exact behavior match wins — curated, high-signal.
    const exact = MEANINGS_BY_BEHAVIOR[state.behavior]
    if (exact) return exact
    // 2) No exact match → reorder MEANINGS_GENERIC so the groups
    //    inferred from the previous columns' text lead.
    const priority = rankMeaningGroupsForContext(state)
    return reorderGroups(MEANINGS_GENERIC, priority)
  }
  if (col === "support") {
    const exact = SUPPORTS_BY_MEANING[state.meaning]
    if (exact) return exact
    // Same trick on the support column — use meaning text to surface
    // relevant support groups first.
    const priority = rankMeaningGroupsForContext({ happening: "", behavior: state.meaning })
    return reorderGroups(SUPPORTS_GENERIC, priority)
  }
  return {}
}

export function hasOverrideFor(col, state) {
  if (col === "behavior") return !!BEHAVIORS_BY_SITUATION[state.happening]
  // For meaning/support we now ALWAYS have an override (either an
  // exact match or a context-reordered generic), so the picker's
  // "showing curated options for…" header shows when relevant.
  if (col === "meaning")  return !!MEANINGS_BY_BEHAVIOR[state.behavior] || !!rankMeaningGroupsForContext(state)
  if (col === "support")  return !!SUPPORTS_BY_MEANING[state.meaning]
  return false
}

export function findOption(col, state, value) {
  const grouped = optionsFor(col, state)
  for (const items of Object.values(grouped)) {
    const hit = items.find(o => o.value === value)
    if (hit) return hit
  }
  return null
}

export function genericFor(col) {
  if (col === "behavior") return BEHAVIORS_GENERIC
  if (col === "meaning")  return MEANINGS_GENERIC
  if (col === "support")  return SUPPORTS_GENERIC
  return SITUATIONS
}

export function quickPicksFor(col, state) {
  const grouped = optionsFor(col, state)
  const all = Object.values(grouped).flat()
  const defaults = QUICK_BY_COL[col] || []
  const ordered = defaults
    .map(v => all.find(o => o.value === v))
    .filter(Boolean)
    .slice(0, 6)
  if (ordered.length < 6) {
    const seen = new Set(ordered.map(o => o.value))
    for (const item of all) {
      if (ordered.length >= 6) break
      if (!seen.has(item.value)) { ordered.push(item); seen.add(item.value) }
    }
  }
  return ordered
}
